import { LanguageService } from '@services/language.service';
import { AppModeEnum, AppState } from '@shared/app-state';
import { StartkitMappingResponse } from './api-models/startkit-mapping-response-model';
import { CustomerModel } from './customer';
import { Order } from './order';
import { OrderStatus } from './order-status';
import { TranslateModel } from './translate-model';
import { UserDataModel } from './user-data-model';

export class OrdersModel {

	private orders: Array<Order>;
	private currentSelectedOrder: Order;

	constructor(
		private appState: AppState,
		orders: Array<Order>
	) {
		this.orders = orders;
	}

	public get allOrders() {
		return this.orders;
	}

	public set currentOrder(order: Order) {
		this.currentSelectedOrder = order;
	}

	public get currentOrder(): Order {
		return this.currentSelectedOrder;
	}

	public removeOrder(order?: Order) {
		this.orders = this.allOrders.filter(x => x.Id !== order?.Id);
	}

	public addOrder(order: Order) {
		this.allOrders.push(order);
	}

	public getIndexOfOrder(order: Order) {
		return this.orders.indexOf(order);
	}

	public getIndexOfOrderId(orderId: number) {
		const order = this.orders.find(x => x.Id === orderId)
		if (order)
			return this.orders.indexOf(order);
		return undefined;
	}

	public get length() {
		return this.allOrders.length;
	}

	AnyLocked(): boolean {
		return this.orders.some(x => x.IsLocked);
	}

	public canBeOrdered(customer: CustomerModel | null, user: UserDataModel | null, startinkitMappings: StartkitMappingResponse[]) {
		const ordersToOrder = this.ordersSharingVisitdate;

		if (ordersToOrder.length === 0) {
			return false;
		}

		return this.validateBlockingOrderRequirements(customer, user, startinkitMappings);
	}

	private getOrdersOnSameVisitDate(visitDate: Date, orderStatus: OrderStatus) {
		return this.allOrders.filter(x => x.VisitDate != null && !x.NoVisit && x.VisitDate.isDateMonthYearEqual(visitDate) && !x.IsLocked);
	}

	private getOrdersSharingNoVisit(orderStatus: OrderStatus) {
		return this.allOrders.filter(x => x.NoVisit === true && !x.IsLocked);
	}

	get ordersSharingVisitdate(): Array<Order> {
		if (!this.currentSelectedOrder) {
			return [];
		}
		else if (this.currentSelectedOrder.anySelected) {
			return [this.currentSelectedOrder];
		}

		if (this.currentSelectedOrder.VisitDate && !this.currentSelectedOrder.NoVisit) {
			return this.getOrdersOnSameVisitDate(this.currentSelectedOrder.VisitDate, this.currentSelectedOrder.Status)
		}
		else if (this.currentSelectedOrder.NoVisit === true) {
			return this.getOrdersSharingNoVisit(this.currentSelectedOrder.Status)
		}
		else {
			return [this.currentSelectedOrder];
		}
	}

	public validateBlockingOrderRequirements(customer: CustomerModel | null, user: UserDataModel | null, startkitMappings: StartkitMappingResponse[]): Array<{ orderId: number, validationResults: Array<TranslateModel> }> {
		if (!this.currentOrder) {
			return [];
		}
		else {
			const allOrdersSharingVisitdate = this.ordersSharingVisitdate;

			let errorTranslationModels = new Array<{ orderId: number, validationResults: Array<TranslateModel> }>();

			allOrdersSharingVisitdate.forEach(order => {
				const validations = order.validatePlaceOrderRequirements(customer, user, allOrdersSharingVisitdate.length > 1, startkitMappings);

				// BUSINESS LOGIC - could be moved to separate business logic/order validation service

				// if (this.appState.appMode.value !== AppModeEnum.default) {

				// }

				// If App Mode is "Repair" (CRM) check that order only contains parts
				// and that these parts are valid for the specific frame the case is handling
				if (this.appState.appMode.value === AppModeEnum.repair) {
					if (order.OrderItems && order.OrderItems.length > 0) {
						// generic 'one or more...'
						if (order.OrderItems.some(oi => (!oi.Parts || (oi.Parts && oi.Parts.length === 0) || oi.IsFullFrame))) {
							validations.push(new TranslateModel('one or more items are not frame parts - only parts are allowed when handling repair'));
						}

						if (order.OrderItems.some(oi => (!oi.SerialNumber || (oi.SerialNumber && oi.SerialNumber !== this.appState.afterSalesSerialNo.value)))) {
							validations.push(new TranslateModel('one or more items have a serial numbers not matching the CRM case serial number'));
						}

						if (order.OrderItems.some(oi => (oi.Amount ?? 0) > 1)) {
							validations.push(new TranslateModel('one or more items have a quantity higher than one (1)'));
						}

						// IF WE WANT TO PINPOINT THE EXACT INVALID ITEM(S) WE CAN DO A LOOP

						// for (let item of order.OrderItems) {
						// 	// all must have value in "Parts" property
						// 	if (!item.Parts || (item.Parts && item.Parts.length === 0) || item.IsFullFrame) {
						// 		validations.push(new TranslateModel('item is not a (spare) part'));
						// 	}

						// 	// all must have same serialnumber as app-state
						// 	if (!item.SerialNumber || (item.SerialNumber && item.SerialNumber !== this.appState.afterSalesSerialNumber.value)) {
						// 		validations.push(new TranslateModel('serial number does not match CRM case serial number'));
						// 	}

						// 	// only quantity of 1 per item
						// 	if (item.Amount > 1) {
						// 		// validations.push(new TranslateModel('only a quantity of 1 per item is accepted'));
						// 	}
						// }
					}
				}
				// Else if App Mode is "Substitution" (CRM) check that the order only contains one order item
				// with a quantity of 1 and that the orderitem is a frame
				else if (this.appState.appMode.value === AppModeEnum.substitution) {
					if (order.OrderItemMiscs && order.OrderItemMiscs.length > 0) {
						validations.push(new TranslateModel('no tool/part items allowed when handling substitution'));
					}
					else {
						if (order.OrderItems.length > 1) {
							validations.push(new TranslateModel('only one item in bag is allowed when handling substitution'));
						}
						else if (order.OrderItems.length === 1) {
							// if (!order.OrderItems[0].IsFullFrame) {
							// 	validations.push(new TranslateModel('only regular frames are allowed when handling substitution'));
							// }

							if ((order.OrderItems[0].Amount ?? 0) > 1) {
								validations.push(new TranslateModel('only a quantity of 1 is accepted when handling substitution'));
							}
						}
					}
				}

				if (validations.length) {
					errorTranslationModels.push({
						orderId: order.Id,
						validationResults: validations
					});
				}
			});

			// checks that we dont have the same productline without linestatus spread across different orders.
			const productlinesWithoutStatusAllOrders = this.allOrders.flatMap(order => order.OrderItems.map(orderItem => {
				let hasProductStatus: boolean;

				if (customer) {
					if (orderItem.IsPrecious && customer.IsPreciousPartner) {
						hasProductStatus = true;
					}
					else {
						hasProductStatus = customer.ProductLinesPermissions?.some(permission => permission === orderItem.ProductLineCode);
					}
				}
				else if (user) {
					hasProductStatus = user.ProductLinesPermissions?.some(permission => permission === orderItem.ProductLineCode) ?? false;
				}
				else {
					hasProductStatus = false;
				}

				return hasProductStatus ? null : { id: orderItem.OrderId, code: orderItem.ProductLineCode };
			})).filter(x => x !== null);

			const uniqueProductCodes = [...new Set(productlinesWithoutStatusAllOrders?.map(item => item.code))];
			uniqueProductCodes.forEach(code => {
				const filteredOrders = productlinesWithoutStatusAllOrders.filter((item) => item.code === code);
				const uniqueIds = [...new Set(filteredOrders.map(item => item.id))];

				if (uniqueIds.length > 1) {
					uniqueIds.forEach(id => {
						errorTranslationModels = errorTranslationModels.map(x => {
							// replace the current errors because the user needs to deal with this "global error" first
							return x.orderId === id ? {
								orderId: x.orderId,
								validationResults: [new TranslateModel("BAG.MIXEDSTARTKITSANDPRODUCTLINES")]
							} : x
						})
					})
				}
			});

			return errorTranslationModels;
		}
	}

	public validateNonBlockingOrderRequirements(customer: CustomerModel | null, user: UserDataModel | null, languageService: LanguageService): Array<{ orderId: number, validationResults: Array<TranslateModel> }> {
		if (!this.currentOrder) {
			return [];
		}
		else {
			const allOrdersSharingVisitdate = this.ordersSharingVisitdate;
			const errorTranslationModel = this.currentOrder.validateNonBlockingOrderRequirements(customer, allOrdersSharingVisitdate.filter(x => x.Uuid != this.currentOrder.Uuid), languageService);

			if (errorTranslationModel.length) {
				const validationErrors = errorTranslationModel.map(e => ({
					orderId: e.orderId,
					validationResults: e.translateModel
				}));

				return validationErrors;
			}

			return [];
		}
	}
}
